import ThemeSectionsDemo from './pages/dev/ThemeSectionsDemo';
import { useEffect, useMemo, useState, type ReactElement, lazy, Suspense } from 'react';
import { isStitchedMicrofrontendPath } from '../lib/microfrontend-routing';
import { navigateWithinSpa, normalizeSpaPath } from '../lib/spa-navigation';
import { registerWebMcpContext } from './lib/webmcp';
import { EventDetailPage, EventFeedbackPage, eventsPageFor } from './lib/events-page';
import { newsPageFor } from './lib/news-page';
import { KonamiPebble } from './components/KonamiPebble';
import './build-timestamp'; // Force bundle invalidation
import { trackPageView, setZarazContext, getOrCreateSessionId } from '../lib/analytics';
import { initializeConsoleFilters } from '../lib/console-filters';

// Initialize console filters (production only)
initializeConsoleFilters();
import {
  Accessibility,
  BookMarked,
  CheckCircle2,
  Clapperboard,
  ClipboardCheck,
  Download,
  FlaskConical,
  Grid3x3,
  Image,
  Info,
  Layers,
  ListTodo,
  MessageCircle,
  MoonStar,
  MousePointerClick,
  Package,
  Palette,
  Puzzle,
  Rainbow,
  Rocket,
  Ruler,
  Send,
  Shapes,
  Sparkles,
  TextCursorInput,
  Volume2,
} from 'lucide-react';
import { FloatingActionBar, ThemeSwitcher, type FloatingAction } from '@sbs/ui-core';
import { getCurrentTheme } from '@smallbiz/brand-kit/themes';
import { usePageTheme } from './hooks/usePageTheme';
import { Footer } from './components/Footer';
import { Button } from '@smallbiz/ui-primitives';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@smallbiz/ui-primitives';
import { SBIPublicHeader } from './components/SBIPublicHeader';
import { ProtectedRoute } from './components/ProtectedRoute';
import { HomePage } from './pages/HomePage';
const AboutPage = lazy(() => import('./pages/AboutPage').then((m) => ({ default: m.AboutPage })));
const ServicesPage = lazy(() =>
  import('./pages/ServicesPage').then((m) => ({ default: m.ServicesPage }))
);
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage }))
);
const GetStartedPage = lazy(() =>
  import('./pages/GetStartedPage').then((m) => ({ default: m.GetStartedPage }))
);
const BlogsPage = lazy(() => import('./pages/BlogsPage').then((m) => ({ default: m.BlogsPage })));
const BlogDetailPage = lazy(() =>
  import('./pages/BlogDetailPage').then((m) => ({ default: m.BlogDetailPage }))
);
const GlossaryPage = lazy(() =>
  import('./pages/GlossaryPage').then((m) => ({ default: m.GlossaryPage }))
);
const GlossaryDetailPage = lazy(() =>
  import('./pages/GlossaryDetailPage').then((m) => ({ default: m.GlossaryDetailPage }))
);
const ToolboxPage = lazy(() =>
  import('./pages/ToolboxPage').then((m) => ({ default: m.ToolboxPage }))
);
const ToolboxDetailPage = lazy(() =>
  import('./pages/ToolboxDetailPage').then((m) => ({ default: m.ToolboxDetailPage }))
);
const NewsPage = lazy(() =>
  import('./pages/NewsPage').then((m) => ({ default: m.NewsPage }))
);
const UpdatesPage = lazy(() =>
  import('./pages/UpdatesPage').then((m) => ({ default: m.UpdatesPage }))
);
const FreebiesPage = lazy(() =>
  import('./pages/FreebiesPage').then((m) => ({ default: m.FreebiesPage }))
);
const MarketingHubPage = lazy(() =>
  import('./pages/MarketingHubPage').then((m) => ({ default: m.MarketingHubPage }))
);
const SalesHubPage = lazy(() =>
  import('./pages/SalesHubPage').then((m) => ({ default: m.SalesHubPage }))
);
const AutomationHubPage = lazy(() =>
  import('./pages/AutomationHubPage').then((m) => ({ default: m.AutomationHubPage }))
);
const FinanceHubPage = lazy(() =>
  import('./pages/FinanceHubPage').then((m) => ({ default: m.FinanceHubPage }))
);
const LeadershipHubPage = lazy(() =>
  import('./pages/LeadershipHubPage').then((m) => ({ default: m.LeadershipHubPage }))
);
const OperationsHubPage = lazy(() =>
  import('./pages/OperationsHubPage').then((m) => ({ default: m.OperationsHubPage }))
);
const StrategyHubPage = lazy(() =>
  import('./pages/StrategyHubPage').then((m) => ({ default: m.StrategyHubPage }))
);
const AssetLibraryPage = lazy(() =>
  import('./pages/AssetLibraryPage').then((m) => ({ default: m.AssetLibraryPage }))
);
const ColorsPage = lazy(() =>
  import('./pages/ColorsPage').then((m) => ({ default: m.ColorsPage }))
);
const AnimationsPage = lazy(() =>
  import('./pages/AnimationsPage').then((m) => ({ default: m.AnimationsPage }))
);
const DevHubPage = lazy(() =>
  import('./pages/dev/DevHubPage').then((m) => ({ default: m.DevHubPage }))
);
const DevRecommendedColorThemesPage = lazy(() =>
  import('./pages/dev/RecommendedColorThemesPage').then((m) => ({
    default: m.DevRecommendedColorThemesPage,
  }))
);
const ReferenceHubPage = lazy(() =>
  import('./pages/reference/ReferenceHubPage').then((m) => ({ default: m.ReferenceHubPage }))
);
const ReferenceRecommendedColorThemesPage = lazy(() =>
  import('./pages/reference/ReferenceRecommendedColorThemesPage').then((m) => ({
    default: m.ReferenceRecommendedColorThemesPage,
  }))
);
const PortfolioDetailPage = lazy(() =>
  import('./pages/PortfolioDetailPage').then((m) => ({ default: m.PortfolioDetailPage }))
);
const TeamPage = lazy(() => import('./pages/TeamPage').then((m) => ({ default: m.TeamPage })));
// #4918 — events (webinars) list + detail.
const EventsPage = lazy(() => import('./pages/EventsPage').then((m) => ({ default: m.EventsPage })));
const TeamDetailPage = lazy(() =>
  import('./pages/TeamDetailPage').then((m) => ({ default: m.TeamDetailPage }))
);
// Admin pages removed from www-sbi bundle (Phase 5.7).
// All /admin/* paths are stitched to admin-sbi worker via service binding
// in microfrontend-routing.ts — the SPA never handles these routes.
const ContentPreviewPage = lazy(() =>
  import('./pages/ContentPreviewPage').then((m) => ({ default: m.ContentPreviewPage }))
);
const HVACTuneUpPage = lazy(() =>
  import('./pages/HVACTuneUpPage').then((m) => ({ default: m.HVACTuneUpPage }))
);
const GeneralLandingPage = lazy(() =>
  import('./pages/GeneralLandingPage').then((m) => ({ default: m.GeneralLandingPage }))
);
const GeneralLandingPageV5 = lazy(() =>
  import('./pages/GeneralLandingPageV5').then((m) => ({ default: m.GeneralLandingPageV5 }))
);
const FieldServiceTuneUpPage = lazy(() =>
  import('./pages/FieldServiceTuneUpPage').then((m) => ({ default: m.FieldServiceTuneUpPage }))
);
const OnDemandSupportPage = lazy(() =>
  import('./pages/OnDemandSupportPage').then((m) => ({ default: m.OnDemandSupportPage }))
);
const SupportDeskPage = lazy(() =>
  import('./pages/SupportDeskPage').then((m) => ({ default: m.SupportDeskPage }))
);
const VirtualStrategySessionsPage = lazy(() =>
  import('./pages/VirtualStrategySessionsPage').then((m) => ({
    default: m.VirtualStrategySessionsPage,
  }))
);
const ProgramsPage = lazy(() =>
  import('./pages/ProgramsPage').then((m) => ({ default: m.ProgramsPage }))
);
const FAQsPage = lazy(() => import('./pages/FAQsPage').then((m) => ({ default: m.FAQsPage })));
const LoginPage = lazy(() =>
  import('./pages/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const AuthConfirmPage = lazy(() =>
  import('./pages/AuthConfirmPage').then((m) => ({ default: m.AuthConfirmPage }))
);
const ResetPasswordPage = lazy(() =>
  import('./pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage }))
);
const AuthVerifyPage = lazy(() =>
  import('./pages/AuthVerifyPage').then((m) => ({ default: m.AuthVerifyPage }))
);
const WelcomePage = lazy(() =>
  import('./pages/WelcomePage').then((m) => ({ default: m.WelcomePage }))
);
const ClientPortalPage = lazy(() =>
  import('./pages/ClientPortalPage').then((m) => ({ default: m.ClientPortalPage }))
);
const FreeConsultsPage = lazy(() =>
  import('./pages/FreeConsultsPage').then((m) => ({ default: m.FreeConsultsPage }))
);
const NewsletterPage = lazy(() =>
  import('./pages/NewsletterPage').then((m) => ({ default: m.NewsletterPage }))
);
const ThankYouPage = lazy(() =>
  import('./pages/ThankYouPage').then((m) => ({ default: m.ThankYouPage }))
);
const ThankYouSignUpPage = lazy(() =>
  import('./pages/ThankYouSignUpPage').then((m) => ({ default: m.ThankYouSignUpPage }))
);
const ThankYouProgramsPage = lazy(() =>
  import('./pages/ThankYouProgramsPage').then((m) => ({ default: m.ThankYouProgramsPage }))
);
const ThankYouFreeConsultPage = lazy(() =>
  import('./pages/ThankYouFreeConsultPage').then((m) => ({ default: m.ThankYouFreeConsultPage }))
);
const ThankYouContactPage = lazy(() =>
  import('./pages/ThankYouContactPage').then((m) => ({ default: m.ThankYouContactPage }))
);
const ThankYouNewsletterPage = lazy(() =>
  import('./pages/ThankYouNewsletterPage').then((m) => ({ default: m.ThankYouNewsletterPage }))
);
const PoliciesPage = lazy(() =>
  import('./pages/PoliciesPage').then((m) => ({ default: m.PoliciesPage }))
);
const TermsPage = lazy(() => import('./pages/TermsPage').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() =>
  import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage }))
);
const CookiesPage = lazy(() =>
  import('./pages/CookiesPage').then((m) => ({ default: m.CookiesPage }))
);
const RefundPage = lazy(() =>
  import('./pages/RefundPage').then((m) => ({ default: m.RefundPage }))
);
const BillingPage = lazy(() =>
  import('./pages/BillingPage').then((m) => ({ default: m.BillingPage }))
);
// import { Toaster } from './components/ui/sonner'; // Removed - missing dependencies

const SchedulingLetsChatPage = lazy(() =>
  import('./pages/SchedulingLetsChatPage').then((m) => ({ default: m.SchedulingLetsChatPage }))
);
const SchedulingBenPage = lazy(() =>
  import('./pages/SchedulingBenPage').then((m) => ({ default: m.SchedulingBenPage }))
);
const SchedulingBenThanksPage = lazy(() =>
  import('./pages/SchedulingBenThanksPage').then((m) => ({ default: m.SchedulingBenThanksPage }))
);
const SchedulingVirtualStrategyPage = lazy(() =>
  import('./pages/SchedulingVirtualStrategyPage').then((m) => ({
    default: m.SchedulingVirtualStrategyPage,
  }))
);
const LightboxesDesignPage = lazy(() =>
  import('./pages/design/LightboxesDesignPage').then((m) => ({ default: m.LightboxesDesignPage }))
);
const CMSContentGridPage = lazy(() =>
  import('./pages/design/CMSContentGridPage').then((m) => ({ default: m.CMSContentGridPage }))
);
const NotFoundPage = lazy(() =>
  import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const PricingPage = lazy(() =>
  import('./pages/PricingPage').then((m) => ({ default: m.PricingPage }))
);
const TestimonialsPage = lazy(() =>
  import('./pages/TestimonialsPage').then((m) => ({ default: m.TestimonialsPage }))
);
const ResourcesIndexPage = lazy(() =>
  import('./pages/ResourcesIndexPage').then((m) => ({ default: m.ResourcesIndexPage }))
);
const FreebieDetailPage = lazy(() =>
  import('./pages/FreebieDetailPage').then((m) => ({ default: m.FreebieDetailPage }))
);
const UpdateDetailPage = lazy(() =>
  import('./pages/UpdateDetailPage').then((m) => ({ default: m.UpdateDetailPage }))
);
const ResourceDetailPage = lazy(() =>
  import('./pages/ResourceDetailPage').then((m) => ({ default: m.ResourceDetailPage }))
);
const AILandingPage = lazy(() =>
  import('./pages/AILandingPage').then((m) => ({ default: m.AILandingPage }))
);
const IronTrianglePage = lazy(() =>
  import('./pages/IronTrianglePage').then((m) => ({ default: m.IronTrianglePage }))
);
const IronTriangleWidgetPage = lazy(() =>
  import('./pages/IronTriangleWidgetPage').then((m) => ({ default: m.IronTriangleWidgetPage }))
);
const SearchPage = lazy(() =>
  import('./pages/SearchPage').then((m) => ({ default: m.SearchPage }))
);
const BrandKitPage = lazy(() =>
  import('./pages/BrandKitPage').then((m) => ({ default: m.BrandKitPage }))
);
const DesignSystemPage = lazy(() =>
  import('./pages/DesignSystemPage').then((m) => ({ default: m.DesignSystemPage }))
);

type PageConfig = {
  name: string;
  path: string;
  component: ReactElement;
  actions: FloatingAction[];
  includeInNav?: boolean;
  /** When true, the page is rendered without the site header and footer (e.g. /widget/* routes for iframe embeds) */
  isWidget?: boolean;
  /** #10764: the page renders its own header and footer (the Night Flight homepage). */
  ownChrome?: boolean;
};

type HeaderNavItem = {
  name: string;
  href: string;
  isActive: boolean;
  icon?: typeof Sparkles;
  description?: string;
  external?: boolean;
};

const brandKitActions: FloatingAction[] = [
  { id: 'overview', icon: Sparkles, label: '✨ Overview', href: '#overview' },
  { id: 'logo', icon: Send, label: '🛫 Logo', href: '#logo' },
  { id: 'brand-kit', icon: Palette, label: '🎨 Color Palette', href: '#brand-kit' },
  { id: 'core-colors', icon: Grid3x3, label: '🧱 Core Combos', href: '#core-colors' },
  { id: 'gradients', icon: Rainbow, label: '🌈 Gradients', href: '#gradients' },
  { id: 'iconography', icon: Shapes, label: '🔣 Iconography', href: '#iconography' },
  { id: 'imagery', icon: Image, label: '🖼️ Imagery', href: '#imagery' },
  { id: 'emoji-messaging', icon: MessageCircle, label: '😊 Emoji Guide', href: '#emoji-messaging' },
  { id: 'accessibility', icon: Accessibility, label: '♿ Accessibility', href: '#accessibility' },
  { id: 'dark-mode', icon: MoonStar, label: '🌙 Dark Mode', href: '#dark-mode' },
];

const designSystemActions: FloatingAction[] = [
  {
    id: 'design-system-overview',
    icon: Sparkles,
    label: '✨ Overview',
    href: '#design-system-overview',
  },
  { id: 'components', icon: Puzzle, label: '🧩 Components', href: '#components' },
  { id: 'spacing-layout', icon: Ruler, label: '📏 Spacing & Layout', href: '#spacing-layout' },
  { id: 'elevation', icon: Layers, label: '🏗️ Elevation', href: '#elevation' },
  { id: 'stylesheets', icon: Download, label: '📦 Code & Stylesheets', href: '#stylesheets' },
];

const aboutActions: FloatingAction[] = [
  { id: 'about-overview', icon: Sparkles, label: '✨ Overview', href: '#about-overview' },
  { id: 'about', icon: Info, label: 'ℹ️ About Us', href: '#about' },
  { id: 'brand-voice', icon: Volume2, label: '🗣️ Brand Voice', href: '#brand-voice' },
];

const assetLibraryActions: FloatingAction[] = [
  { id: 'asset-library', icon: Package, label: '📁 Asset Library', href: '#asset-library' },
];

const colorsActions: FloatingAction[] = [
  { id: 'colors-overview', icon: Sparkles, label: '✨ Overview', href: '#colors-overview' },
  { id: 'colors-palette', icon: Palette, label: '🎨 Palette', href: '#colors-palette' },
  {
    id: 'colors-combos',
    icon: Grid3x3,
    label: '🧱 Combinations',
    href: '#core-color-combinations',
  },
  { id: 'colors-showcase', icon: Rainbow, label: '🌈 Showcase', href: '#colors-showcase' },
  {
    id: 'colors-theme-sections',
    icon: Layers,
    label: '🏗️ Theme sections',
    href: '#colors-theme-sections',
  },
  {
    id: 'colors-implementation',
    icon: ClipboardCheck,
    label: '✅ Implementation',
    href: '#colors-implementation',
  },
  { id: 'aria-patterns', icon: Accessibility, label: '♿ ARIA Patterns', href: '#aria-patterns' },
];

const animationsActions: FloatingAction[] = [
  { id: 'animations-overview', icon: Sparkles, label: '✨ Overview', href: '#animations-overview' },
  { id: 'gsap-setup', icon: Clapperboard, label: '🎬 GSAP Setup', href: '#gsap-setup' },
  { id: 'typing-hero', icon: TextCursorInput, label: '⌨️ Typing Hero', href: '#typing-hero' },
  { id: 'lollies', icon: Rocket, label: '🚀 Lollies', href: '#lollies' },
  { id: 'ux-animations', icon: MousePointerClick, label: '🖱️ Micro UX', href: '#ux-animations' },
];

const devHubActions: FloatingAction[] = [
  { id: 'dev-overview', icon: FlaskConical, label: '🧪 Why prototypes', href: '#dev-overview' },
  { id: 'dev-catalog', icon: ListTodo, label: '📋 Active experiments', href: '#dev-catalog' },
];

const devRecommendedActions: FloatingAction[] = [
  {
    id: 'dev-recommended-overview',
    icon: Sparkles,
    label: '✨ Overview',
    href: '#dev-recommended-overview',
  },
  {
    id: 'dev-recommended-gallery',
    icon: Palette,
    label: '🎨 Theme gallery',
    href: '#dev-recommended-gallery',
  },
  {
    id: 'dev-recommended-next',
    icon: ClipboardCheck,
    label: '✅ Next steps',
    href: '#dev-recommended-next',
  },
];

const referenceHubActions: FloatingAction[] = [
  { id: 'reference-overview', icon: BookMarked, label: '📚 Overview', href: '#reference-overview' },
  { id: 'reference-catalog', icon: ListTodo, label: '🗂️ Ready pages', href: '#reference-catalog' },
];

const referenceRecommendedActions: FloatingAction[] = [
  {
    id: 'reference-recommended-overview',
    icon: Sparkles,
    label: '✨ Overview',
    href: '#reference-recommended-overview',
  },
  {
    id: 'reference-recommended-gallery',
    icon: Palette,
    label: '🎨 Approved presets',
    href: '#reference-recommended-gallery',
  },
  {
    id: 'reference-recommended-signoff',
    icon: CheckCircle2,
    label: '🛫 Launch checklist',
    href: '#reference-recommended-signoff',
  },
];

const pages: PageConfig[] = [
  // Main marketing pages (visible in nav)
  { name: 'Home', path: '/', component: <HomePage />, actions: [], includeInNav: true, ownChrome: true },
  { name: 'Login', path: '/login', component: <LoginPage />, actions: [], includeInNav: false },
  { name: 'Auth Confirm', path: '/auth/confirm', component: <AuthConfirmPage />, actions: [], includeInNav: false },
  { name: 'Auth Verify', path: '/auth/verify', component: <AuthVerifyPage />, actions: [], includeInNav: false },
  { name: 'Reset Password', path: '/auth/reset-password', component: <ResetPasswordPage />, actions: [], includeInNav: false },
  { name: 'Welcome', path: '/welcome', component: <WelcomePage />, actions: [], includeInNav: false },
  {
    name: 'Dashboard',
    path: '/dashboard',
    component: <ProtectedRoute><ClientPortalPage /></ProtectedRoute>,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Portal Requests',
    path: '/portal/requests',
    component: <ProtectedRoute><ClientPortalPage /></ProtectedRoute>,
    actions: [],
    includeInNav: false,
  },
  { name: 'About', path: '/about', component: <AboutPage />, actions: [], includeInNav: true },
  {
    name: 'Services',
    path: '/services',
    component: <ServicesPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Contact',
    path: '/contact',
    component: <ContactPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Get Started',
    path: '/get-started',
    component: <GetStartedPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Search',
    path: '/search',
    component: <SearchPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Free Consults',
    path: '/free-consults',
    component: <FreeConsultsPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Newsletter Archive',
    path: '/news',
    component: <NewsPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Newsletter',
    path: '/newsletter',
    component: <NewsletterPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Thank You',
    path: '/thank-you',
    component: <ThankYouPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Newsletter Success',
    path: '/thank-you-sign-up',
    component: <ThankYouSignUpPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Programs Signup Success',
    path: '/thank-you/programs',
    component: <ThankYouProgramsPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Free Consult Booking Success',
    path: '/thank-you/free-consult',
    component: <ThankYouFreeConsultPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Contact Message Received',
    path: '/thank-you/contact',
    component: <ThankYouContactPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Newsletter Subscription Confirmed',
    path: '/thank-you/newsletter',
    component: <ThankYouNewsletterPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Sign Up Success (Alias)',
    path: '/sign-up-thanks',
    component: <ThankYouSignUpPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Scheduling Success',
    path: '/scheduling-thanks',
    component: <ThankYouFreeConsultPage />,
    actions: [],
    includeInNav: false,
  },

  // Supporting service pages (SEO-critical)
  {
    name: 'Programs',
    path: '/programs',
    component: <ProgramsPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Pricing',
    path: '/pricing',
    component: <PricingPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Testimonials',
    path: '/testimonials',
    component: <TestimonialsPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'AI Content API',
    path: '/ai',
    component: <AILandingPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'On-Demand Support',
    path: '/on-demand-support',
    component: <OnDemandSupportPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Support Desk',
    path: '/support-desk',
    component: <SupportDeskPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Virtual Strategy Sessions',
    path: '/virtual-strategy-sessions',
    component: <VirtualStrategySessionsPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'FAQs',
    path: '/faqs',
    component: <FAQsPage />,
    actions: [],
    includeInNav: false,
  },

  // Content collection pages (SEO-critical, now in mobile nav)
  {
    name: 'Blog',
    path: '/blog',
    component: <BlogsPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Blog Detail',
    path: '/blog/:slug',
    component: <BlogDetailPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Glossary',
    path: '/glossary',
    component: <GlossaryPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Glossary Detail',
    path: '/glossary/:slug',
    component: <GlossaryDetailPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Team',
    path: '/team',
    component: <TeamPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Team Detail',
    path: '/team/:slug',
    component: <TeamDetailPage />,
    actions: [],
    includeInNav: false,
  },
  // #4918 — events (webinars).
  {
    name: 'Events',
    path: '/events',
    component: <EventsPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Event Detail',
    path: '/events/:slug',
    component: <EventDetailPage />,
    actions: [],
    includeInNav: false,
  },
  // #4923 — post-event feedback form (linked from the follow-up email).
  {
    name: 'Event Feedback',
    path: '/events/:slug/feedback',
    component: <EventFeedbackPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Toolbox',
    path: '/toolbox',
    component: <ToolboxPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Toolbox Detail',
    path: '/toolbox/:slug',
    component: <ToolboxDetailPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Updates',
    path: '/updates',
    component: <UpdatesPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Freebies',
    path: '/freebies',
    component: <FreebiesPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Resources',
    path: '/resources',
    component: <ResourcesIndexPage />,
    actions: [],
    includeInNav: true,
  },

  // Content hub pages (now in mobile nav)
  {
    name: 'Marketing Hub',
    path: '/marketing',
    component: <MarketingHubPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Sales Hub',
    path: '/sales',
    component: <SalesHubPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Automation Hub',
    path: '/automation',
    component: <AutomationHubPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Finance Hub',
    path: '/finance',
    component: <FinanceHubPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Leadership Hub',
    path: '/leadership',
    component: <LeadershipHubPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Operations Hub',
    path: '/operations',
    component: <OperationsHubPage />,
    actions: [],
    includeInNav: true,
  },
  {
    name: 'Strategy Hub',
    path: '/strategy',
    component: <StrategyHubPage />,
    actions: [],
    includeInNav: true,
  },

  // Design system pages (unlinked, accessible at /design/*)
  {
    name: 'Brand Kit',
    path: '/design/brand-kit',
    component: <BrandKitPage />,
    actions: brandKitActions,
    includeInNav: false,
  },
  {
    name: 'Colors',
    path: '/design/colors',
    component: <ColorsPage />,
    actions: colorsActions,
    includeInNav: false,
  },
  {
    name: 'Design System',
    path: '/design/system',
    component: <DesignSystemPage />,
    actions: designSystemActions,
    includeInNav: false,
  },
  {
    name: 'Animations',
    path: '/design/animations',
    component: <AnimationsPage />,
    actions: animationsActions,
    includeInNav: false,
  },
  {
    name: 'Asset Library',
    path: '/design/assets',
    component: <AssetLibraryPage />,
    actions: assetLibraryActions,
    includeInNav: false,
  },
  {
    name: 'Dev Sandbox',
    path: '/dev',
    component: <DevHubPage />,
    actions: devHubActions,
    includeInNav: false,
  },
  {
    name: 'Dev – Recommended Themes',
    path: '/dev/recommended-color-themes',
    component: <DevRecommendedColorThemesPage />,
    actions: devRecommendedActions,
    includeInNav: false,
  },
  {
    name: 'Dev – Theme Sections',
    path: '/dev/theme-sections',
    component: <ThemeSectionsDemo />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Reference Hub',
    path: '/reference',
    component: <ReferenceHubPage />,
    actions: referenceHubActions,
    includeInNav: false,
  },
  {
    name: 'Reference – Recommended Themes',
    path: '/reference/recommended-color-themes',
    component: <ReferenceRecommendedColorThemesPage />,
    actions: referenceRecommendedActions,
    includeInNav: false,
  },

  // Easter Eggs (unlisted)
  {
    name: 'The Iron Triangle',
    path: '/stage/the-iron-triangle',
    component: <IronTrianglePage />,
    actions: [],
    includeInNav: false,
  },

  // Admin routes removed (Phase 5.7) — served by admin-sbi via service binding.

  // Campaign Landing Pages
  {
    name: 'HVAC Business Tune-Up',
    path: '/hvac-business-tune-up',
    component: <HVACTuneUpPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Field Service Tune-Up',
    path: '/field-service-tune-up',
    component: <FieldServiceTuneUpPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'General Landing Page - March 2026',
    path: '/p/0326',
    component: <GeneralLandingPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'General Landing Page V5 - March 2026',
    path: '/p/0326-v5',
    component: <GeneralLandingPageV5 />,
    actions: [],
    includeInNav: false,
  },

  // Policy Pages
  {
    name: 'Policies',
    path: '/policies',
    component: <PoliciesPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Terms of Service',
    path: '/terms',
    component: <TermsPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Privacy Policy',
    path: '/privacy',
    component: <PrivacyPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Cookie Policy',
    path: '/cookies',
    component: <CookiesPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Refund Policy',
    path: '/refund',
    component: <RefundPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Billing Policy',
    path: '/billing',
    component: <BillingPage />,
    actions: [],
    includeInNav: false,
  },
  // Scheduling Pages (Internal)
  {
    name: "Let's Chat",
    path: '/scheduling/lets-chat',
    component: <SchedulingLetsChatPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: "Let's Chat",
    path: '/lets-chat',
    component: <SchedulingLetsChatPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Virtual Strategy Session Scheduling',
    path: '/scheduling/virtual-strategy',
    component: <SchedulingVirtualStrategyPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Book Time with Ben',
    path: '/scheduling/ben',
    component: <SchedulingBenPage />,
    actions: [],
    includeInNav: false,
  },
  {
    // #3652: TidyCal after-booking redirect for Ben's 30bw booking type.
    name: 'Booked with Ben',
    path: '/scheduling/ben/thanks',
    component: <SchedulingBenThanksPage />,
    actions: [],
    includeInNav: false,
  },

  // Design System Tests
  {
    name: 'Design - Lightboxes',
    path: '/design/lightboxes',
    component: <LightboxesDesignPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'CMS Content Grid',
    path: '/design/cms-grid',
    component: <CMSContentGridPage />,
    actions: [],
    includeInNav: false,
  },
  {
    name: 'Design - Lightboxes',
    path: '/design/lightboxes',
    component: <LightboxesDesignPage />,
    actions: [],
    includeInNav: false,
  },
];

export default function App() {
  const [currentPath, setCurrentPath] = useState(() => {
    if (typeof window === 'undefined') {
      return '/';
    }

    return normalizeSpaPath(window.location.pathname);
  });

  // Apply page-specific theme based on current route (CSS handles light/dark mode transitions)
  usePageTheme(currentPath);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(normalizeSpaPath(window.location.pathname));
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isJsdom =
        typeof window.navigator !== 'undefined' && window.navigator.userAgent.includes('jsdom');

      if (isJsdom) {
        return;
      }

      const { scrollTo } = window;

      if (typeof scrollTo === 'function') {
        try {
          scrollTo({ top: 0, behavior: 'auto' });
        } catch {
          scrollTo(0, 0);
        }
      }
    }
  }, [currentPath]);

  useEffect(() => {
    registerWebMcpContext(currentPath).catch((error) => {
      console.warn('WebMCP context registration failed', error);
    });
  }, [currentPath]);

  const currentPage = useMemo(() => {
    // Handle /widget/* routes — rendered without site nav or footer
    if (currentPath === '/widget/iron-triangle') {
      return {
        name: 'Iron Triangle Widget',
        path: currentPath,
        component: <IronTriangleWidgetPage />,
        actions: [],
        isWidget: true,
      };
    }

    // Handle dynamic freebies/:slug routes
    if (currentPath.startsWith('/freebies/') && currentPath !== '/freebies') {
      const slug = currentPath.replace('/freebies/', '');
      return {
        name: 'Freebie Detail',
        path: currentPath,
        component: <FreebieDetailPage />,
        actions: [],
      };
    }

    // Handle dynamic updates/:slug routes
    if (currentPath.startsWith('/updates/') && currentPath !== '/updates') {
      const slug = currentPath.replace('/updates/', '');
      return {
        name: 'Update Detail',
        path: currentPath,
        component: <UpdateDetailPage />,
        actions: [],
      };
    }

    // Handle dynamic resources/:slug routes (hub resources detail)
    if (currentPath.startsWith('/resources/') && currentPath !== '/resources') {
      const slug = currentPath.replace('/resources/', '');
      return {
        name: 'Resource Detail',
        path: currentPath,
        component: <ResourceDetailPage />,
        actions: [],
      };
    }

    // Handle /portfolio and /portfolio/:slug — both render from the unified
    // content API (/api/content/portfolio), the same source /about reads. No
    // redirect to the parallel portfolio.smallbizinnovation.com worker (#1035).
    if (currentPath === '/portfolio' || currentPath.startsWith('/portfolio/')) {
      const slug = currentPath.startsWith('/portfolio/')
        ? currentPath.replace('/portfolio/', '')
        : '';
      return {
        name: 'Portfolio',
        path: currentPath,
        component: <PortfolioDetailPage slug={slug} />,
        actions: [],
      };
    }

    // Handle dynamic team/:slug routes
    if (currentPath.startsWith('/team/') && currentPath !== '/team') {
      const slug = currentPath.replace('/team/', '');
      return {
        name: 'Team Member',
        path: currentPath,
        component: <TeamDetailPage slug={slug} />,
        actions: [],
      };
    }

    // Handle dynamic blog/:slug routes (both /blog/ and /blogs/)
    if (currentPath.startsWith('/blog/') && currentPath !== '/blog') {
      return {
        name: 'Blog Detail',
        path: currentPath,
        component: <BlogDetailPage />,
        actions: [],
      };
    }
    if (
      currentPath.startsWith('/blogs/') &&
      currentPath !== '/blogs' &&
      currentPath !== '/blogs-and-articles'
    ) {
      return {
        name: 'Blog Detail',
        path: currentPath,
        component: <BlogDetailPage />,
        actions: [],
      };
    }

    // #4918/#4923 — /events/:slug and /events/:slug/feedback (pages[] matches exact paths only).
    const eventsPage = eventsPageFor(currentPath);
    if (eventsPage) return eventsPage;

    // #6402 N2 — /news/:id (one newsletter issue; pages[] matches exact paths only).
    const newsPage = newsPageFor(currentPath);
    if (newsPage) return newsPage;

    // Handle dynamic glossary/:slug routes
    if (currentPath.startsWith('/glossary/') && currentPath !== '/glossary') {
      return {
        name: 'Glossary Detail',
        path: currentPath,
        component: <GlossaryDetailPage />,
        actions: [],
      };
    }

    // Handle dynamic toolbox/:slug routes
    if (currentPath.startsWith('/toolbox/') && currentPath !== '/toolbox') {
      return {
        name: 'Toolbox Detail',
        path: currentPath,
        component: <ToolboxDetailPage />,
        actions: [],
      };
    }

    // Handle dynamic preview routes
    if (currentPath.startsWith('/preview/')) {
      const pathParts = currentPath.split('/').filter(Boolean);
      // /preview/:collection/:slug
      if (pathParts.length === 3) {
        const collection = pathParts[1];
        const slug = pathParts[2];

        if (['blog', 'updates', 'freebies', 'team', 'glossary'].includes(collection)) {
          return {
            name: 'Preview',
            path: currentPath,
            component: <ContentPreviewPage collection={collection} slug={slug} />,
            actions: [],
          };
        }
      }
    }

    // Check if the path matches any registered page
    const matchedPage = pages.find((page) => page.path === currentPath);

    // If no match found, return 404 page
    if (!matchedPage) {
      return {
        name: '404 Not Found',
        path: currentPath,
        component: <NotFoundPage />,
        actions: [],
      };
    }

    return matchedPage;
  }, [currentPath]);

  const handleNavigate = (href: string) => {
    const nextPath = normalizeSpaPath(href);
    const nextPathname = nextPath.split('?')[0] ?? '/';

    if (nextPath === currentPath) {
      return;
    }

    // Stitched microfrontend paths (/glossary/*, /tips/*, /admin/*) are served by
    // separate workers (astro-sbi, admin-sbi) via Cloudflare service bindings.
    // SPA pushState to these paths renders the React stub component instead of the
    // worker-served Astro/admin page. Force a full HTTP navigation for correctness.
    if (isStitchedMicrofrontendPath(nextPathname)) {
      window.location.href = nextPath;
      return;
    }

    navigateWithinSpa(nextPath);
  };

  // Seed Zaraz context once on mount — provides session_id to the Context Enricher
  // so all events within this tab session get the same stable visitor_id.
  // Do not emit custom nytro trigger events here. Tool loading is configured in Zaraz.
  useEffect(() => {
    setZarazContext({ sessionId: getOrCreateSessionId() });
  }, []);

  // NOTE: The meta dedup useEffect was removed (2026-04-15, issue #369 follow-up).
  // It called meta.parentNode?.removeChild(meta) directly on DOM nodes owned by
  // react-helmet-async, corrupting Helmet's internal node references. On the next
  // navigation Helmet would try to remove those same nodes → parentNode was null →
  // "Cannot read properties of null (reading 'removeChild')" React reconciler crash.
  // NYTRO Static Mode (set 2026-04-15) eliminates the meta-accumulation root cause,
  // making the workaround unnecessary. react-helmet-async deduplicates its own tags.
  // See docs/AUDIT_CRITICAL_NAV_LOCKOUT.md — Issue 4 and Fix #7.

  // Track pageviews on navigation
  useEffect(() => {
    trackPageView(currentPath, {
      page_title: currentPage.name,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
    });
  }, [currentPath, currentPage.name]);

  const navItems: HeaderNavItem[] = pages
    .filter((page) => {
      // No top-level nav items - all nested in dropdowns
      // Logo is the home link
      return false;
    })
    .map((page) => ({
      name: page.name,
      href: page.path,
      isActive: page.path === currentPage.path,
    }));

  // Icon-only theme switcher component
  const IconOnlyThemeSwitcher = () => <ThemeSwitcher variant="icon" />;

  // Widget routes render without the site navigation or footer
  if (currentPage.isWidget) {
    return (
      <div className="bg-background text-foreground transition-colors">
        <Suspense fallback={null}>{currentPage.component}</Suspense>
      </div>
    );
  }

  // #10764: the Night Flight homepage brings its own header and footer, so the site ones stay off on /.
  if ('ownChrome' in currentPage && currentPage.ownChrome) {
    return (
      <div className="min-h-screen bg-background text-foreground transition-colors">
        <Suspense fallback={null}>{currentPage.component}</Suspense>
        <KonamiPebble />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors">
      <SBIPublicHeader navItems={navItems} onNavigate={handleNavigate} />

      {/* Main content with top padding to prevent header overlap */}
      <div className="pt-20 sm:pt-24">
        <Suspense
          fallback={
            <div className="mx-auto max-w-7xl px-4 py-12 text-foreground/70" aria-live="polite">
              Loading page...
            </div>
          }
        >
          {currentPage.component}
        </Suspense>
      </div>

      <Footer />
      {/* <Toaster /> */}
      <KonamiPebble />
    </div>
  );
}
